import { Button, message, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';
import { ReactComponent as LinkIcon } from '../../../../Assets/linkIcon.svg';
import { getMaskedAmount } from '../MaskedAmount';
import { formatToINR } from '../../../../Utils/utils';
import BalanceHeader from '../../Header/BalanceHeader';
import styles from './index.module.css';
import { AppContext, RefreshStatus } from '../../../../App';
import { pushEventToParent } from '../../../../Utils/eventUtils';
import { refreshApi } from '../../../../Services/PFMClient/DashboardService';
import { RefreshDataFetchStatus } from '../../../../Utils/types';

type Props = {
  label: string;
  amount: number;
  isAmountVisible: boolean;
  linkedCount: number;
  linkedLabel: string;
  onLinkedClick?: () => void;
  buttonText: string;
  onButtonClick: () => void;
  showBalanceToggle?: boolean;
  formatAmount?: (amount: number) => React.ReactNode;
  centered?: boolean;
  showRefresh?: boolean;
  showButton?: boolean;
  refreshAccounts?: any[];
};

const AssetTopSection: React.FC<Props> = ({
  label,
  amount,
  isAmountVisible,
  linkedCount,
  linkedLabel,
  onLinkedClick,
  buttonText,
  onButtonClick,
  showBalanceToggle = false,
  formatAmount,
  centered = true,
  showRefresh = false,
  showButton = true,
  refreshAccounts,
}) => {
  const {
    setIsAccountDetailsFetched,
    setStatusPolling,
    setDataRefreshStatus,
    statusData,
    setAnalyticsStatus,
  }: any = useContext(AppContext);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshInfo = useMemo(() => {
    if (!refreshAccounts?.length) return null;

    const lastFetchedTimes = refreshAccounts
      .map((acc) => acc?.lastRawDataFetchedAt)
      .filter(Boolean)
      .map((date) => dayjs(date));
    const lastFetchedAt = lastFetchedTimes.length
      ? lastFetchedTimes.reduce((latest, curr) =>
          curr.isAfter(latest) ? curr : latest
        )
      : null;

    const isRefreshPending = refreshAccounts.some(
      (acc) => acc?.refreshStatus === RefreshDataFetchStatus.PENDING
    );

    const isRefreshFailed = refreshAccounts.some(
      (acc) =>
        acc?.refreshStatus === RefreshDataFetchStatus.REFRESH_FAILED ||
        acc?.refreshStatus === RefreshDataFetchStatus.REFRESH_CALL_FAILED
    );

    const nextRefreshTimes = refreshAccounts
      .map((acc) => acc?.nextRefreshTime)
      .filter(Boolean)
      .map((date) => dayjs(date));
    const nextRefreshTime = nextRefreshTimes.length
      ? nextRefreshTimes.reduce((earliest, curr) =>
          curr.isBefore(earliest) ? curr : earliest
        )
      : null;

    if (!lastFetchedAt && !isRefreshPending && !isRefreshFailed && !nextRefreshTime)
      return null;

    return { lastFetchedAt, isRefreshPending, isRefreshFailed, nextRefreshTime };
  }, [refreshAccounts]);
  const handleRefresh = () => {
    if (
      statusData.current.accountDetails.filter(
        (accountDetail: any) => accountDetail.status === 'REVOKE_INITIATED'
      ).length !== 0
    ) {
      message.info(
        <Typography.Text style={{ fontSize: 10, fontWeight: 600 }}>
          Consent revocation in progress. Please try again in some time.
        </Typography.Text>
      );
      return;
    }
    pushEventToParent({
      eventCode: 'USER_INITIATED_REFRESH_BUTTON_CLICKED',
    });
    setIsRefreshing(true);
    refreshApi()
      .then(() => {
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setStatusPolling((prev: any) => prev + 1);
        setDataRefreshStatus(RefreshStatus.IN_PROGRESS);
        pushEventToParent({ eventCode: 'NPS_REFRESH_PROCESSING' });
      })
      .catch(() => {
        pushEventToParent({ eventCode: 'NPS_REFRESH_FAILED' });
        message.error('Something went wrong. Try again after sometime');
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const content = (
    <div className={centered ? styles.topRowCentered : styles.topRow}>
      <div className={centered ? styles.topCentered : styles.topLeft}>
        <Typography.Text className={styles.topLabel}>{label}</Typography.Text>
        <Typography.Text className={styles.topAmount}>
          {amount ? formatToINR(amount.toString(), isAmountVisible) : 'Loading...'}
        </Typography.Text>
        <Typography.Text className={styles.topSubtitle} onClick={onLinkedClick}>
          <LinkIcon /> {linkedCount} {linkedLabel}
        </Typography.Text>
        {refreshInfo?.isRefreshFailed && (
          <Typography.Text className={styles.refreshFailedText}>
            Last refresh failed
          </Typography.Text>
        )}
        {refreshInfo && (
          <Typography.Text className={styles.refreshInfoText}>
            {refreshInfo.lastFetchedAt &&
              `Updated as on ${refreshInfo.lastFetchedAt.format(
                "DD MMM 'YY"
              )}`}
            {!refreshInfo.isRefreshPending &&
              refreshInfo.nextRefreshTime &&
              ` · Next refresh on ${refreshInfo.nextRefreshTime.format(
                "DD MMM 'YY"
              )}`}
          </Typography.Text>
        )}
      </div>
      {showButton && (
        <div className={centered ? styles.topActions : undefined}>
          <Button className={styles.addBtn} onClick={onButtonClick}>
            {buttonText}
          </Button>
        </div>
      )}
      {showRefresh && (
        <div className={styles.refreshRow}>
          <Typography.Text
            className={styles.refreshLink}
            onClick={handleRefresh}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh now'}
          </Typography.Text>
        </div>
      )}
    </div>
  );

  if (showBalanceToggle) {
    return (
      <div className={styles.topSection}>
        <BalanceHeader>{content}</BalanceHeader>
      </div>
    );
  }

  return <div className={styles.topSection}>{content}</div>;
};

export default AssetTopSection;
